import { useTranslation } from 'react-i18next';
import { SidebarSection, SidebarSectionSubtitle } from '../styles';
import { Button } from '@salecycle/ui-component-library';
import { useMutation, useQueryClient } from 'react-query';
import { useParams } from 'react-router';
import { useTemplatesListDispatch } from '../../context/UseTemplatesListContext';
import useNotifications from 'core/notifications/useNotifications';
import { ITemplate } from 'entities/template/template';
import { TFunction } from 'i18next';
import styled from 'styled-components';

const CloneTemplateButtonContainer = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
`;

export interface CloneTemplateButtonProps {
  setShowModal(show: boolean): void;
  cloneFunction: (params: { siteId: string; templateId: string; type: string; t: TFunction }) => Promise<ITemplate[]>;
  getQueryKeysToInvalidate: () => string;
  selectedTemplate: ITemplate;
}

export const CloneTemplateButton = ({
  setShowModal,
  cloneFunction,
  getQueryKeysToInvalidate,
  selectedTemplate,
}: CloneTemplateButtonProps) => {
  const { t } = useTranslation(['common', 'designs', 'supportedLanguages', 'templates']);
  const { siteId } = useParams<{ siteId: string }>();
  const { showNotification } = useNotifications();
  const dispatch = useTemplatesListDispatch();
  const queryClient = useQueryClient();
  const cloneTemplateMutation = useMutation(cloneFunction);

  const handleClone = () => {
    cloneTemplateMutation.mutate(
      {
        siteId,
        templateId: selectedTemplate.id,
        type: selectedTemplate.type,
        t,
      },
      {
        onSuccess: data => {
          if (!data || data.length === 0) {
            showNotification(t('designs:designGallery.form.errorHeading'), 'error');
            return;
          }

          const clonedTemplate = data[0];
          showNotification(t('designs:designGallery.form.comment.notifications.cloneTemplateSuccess'), 'normal');
          queryClient.invalidateQueries(getQueryKeysToInvalidate());
          dispatch({
            type: 'SET_SELECTED_TEMPLATE',
            payload: {
              template: clonedTemplate,
            },
          });
        },
        onError: () => {
          showNotification(t('designs:designGallery.form.errorHeading'), 'error');
        },
      },
    );

    setShowModal(false);
  };

  return (
    <SidebarSection data-testid="CloneTemplate">
      <SidebarSectionSubtitle data-testid="CloneTemplateSubTitle">
        {t('designs:designGallery.sidebar.cloneSubtitle')}
      </SidebarSectionSubtitle>
      <CloneTemplateButtonContainer>
        <Button variation="primary" onClick={handleClone} width={236}>
          {t('designs:designGallery.sidebar.cloneButton')}
        </Button>
      </CloneTemplateButtonContainer>
    </SidebarSection>
  );
};
